import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { TextInput } from '@/components/ui/text-input';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/use-auth';
import { validatePassword } from '@/utils/auth-validation';
import { createLogger } from '@/utils/logger';
import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, View, type TextInput as RNTextInput } from 'react-native';
import { AuthLink } from './components/auth-link';
import { AuthScreenLayout } from './components/auth-screen-layout';

const logger = createLogger('RESET_PASSWORD');

const SUCCESS_REDIRECT_MS = 2000;

export function ResetPasswordScreen() {
  const { resetPassword } = useAuth();
  const params = useLocalSearchParams<{ token?: string }>();

  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSuccess, setIsSuccess] = useState(false);

  const confirmRef = useRef<RNTextInput>(null);
  const redirectRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (params.token) {
      setToken(params.token);
      return;
    }

    if (process.env.EXPO_OS === 'web' && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      if (tokenFromUrl) {
        setToken(tokenFromUrl);
        return;
      }
    }

    logger.warn('No reset token found in URL or params');
    toast.error('That reset link is invalid or has expired', {
      description: 'Request a new one to choose a new password.',
    });
    router.replace({ pathname: '/(auth)/login' });
  }, [params.token]);

  // The success state navigates on a timer; without this it fires against an
  // unmounted screen if the user leaves first.
  useEffect(() => () => {
    if (redirectRef.current) clearTimeout(redirectRef.current);
  }, []);

  const validateInputs = () => {
    const newErrors: Record<string, string> = {};

    const passwordError = validatePassword(newPassword);
    if (passwordError) newErrors.newPassword = passwordError;

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleResetPassword = async () => {
    if (!validateInputs()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const result = await resetPassword(token, newPassword);

      if (result.error) {
        logger.error('Reset password failed:', result.error);

        // A rejected token is the only thing this endpoint fails on that the user can
        // act upon, and it is not attributable to either field. Route on the status
        // code: the old check sniffed the message for 'token' / 'expired' / 'invalid',
        // so a server copy change would silently reroute the UI.
        const isRejectedToken =
          result.error.status === 400 ||
          result.error.status === 401 ||
          result.error.status === 403;

        if (isRejectedToken) {
          toast.error('That reset link is invalid or has expired', {
            description: 'Request a new one to choose a new password.',
          });
          router.replace({ pathname: '/(auth)/forgot-password' });
        } else {
          toast.error('Could not reset your password', {
            description: 'Something went wrong on our end. Try again in a moment.',
          });
        }
      } else {
        logger.log('Password reset successful');
        setIsSuccess(true);
        redirectRef.current = setTimeout(
          () => router.replace({ pathname: '/(auth)/login' }),
          SUCCESS_REDIRECT_MS
        );
      }
    } catch {
      toast.error('Could not reset your password', {
        description: 'Check your connection and try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Rendered inside the shared layout so the wait doesn't flash different chrome
  // before the form mounts.
  if (!token && !isSuccess) {
    return (
      <AuthScreenLayout title="Reset Password">
        <View className="items-center gap-3" accessibilityLiveRegion="polite">
          <ActivityIndicator accessibilityLabel="Checking your reset link" />
          <Text className="text-sm text-warm-gray-300">Checking your reset link...</Text>
        </View>
      </AuthScreenLayout>
    );
  }

  if (isSuccess) {
    return (
      <AuthScreenLayout title="Reset Password" heading="Password reset">
        <Text
          className="text-base text-center text-warm-gray-300"
          accessibilityLiveRegion="polite"
        >
          Your password has been reset. Taking you to log in...
        </Text>
      </AuthScreenLayout>
    );
  }

  return (
    <AuthScreenLayout
      title="Reset Password"
      heading="Reset your password"
      subheading="Choose a new password below"
      footer={
        <View className="flex-row justify-center">
          <AuthLink
            onPress={() => router.replace({ pathname: '/(auth)/login' })}
            testID="reset-login-link"
          >
            Back to log in
          </AuthLink>
        </View>
      }
    >
      <View className="gap-4">
        <TextInput
          placeholder="New Password"
          value={newPassword}
          onChangeText={(text) => {
            setNewPassword(text);
            if (errors.newPassword) setErrors((prev) => ({ ...prev, newPassword: '' }));
          }}
          secureTextEntry={!showPassword}
          autoCapitalize="none"
          autoCorrect={false}
          spellCheck={false}
          autoComplete="new-password"
          textContentType="newPassword"
          returnKeyType="next"
          onSubmitEditing={() => confirmRef.current?.focus()}
          submitBehavior="submit"
          editable={!isLoading}
          error={errors.newPassword || undefined}
          rightIcon={
            <AuthLink
              tone="muted"
              onPress={() => setShowPassword(!showPassword)}
              testID="reset-toggle-password"
              accessibilityLabel={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? 'Hide' : 'Show'}
            </AuthLink>
          }
        />

        <TextInput
          ref={confirmRef}
          placeholder="Confirm Password"
          value={confirmPassword}
          onChangeText={(text) => {
            setConfirmPassword(text);
            if (errors.confirmPassword) setErrors((prev) => ({ ...prev, confirmPassword: '' }));
          }}
          secureTextEntry={!showConfirmPassword}
          autoCapitalize="none"
          autoCorrect={false}
          spellCheck={false}
          autoComplete="new-password"
          textContentType="newPassword"
          returnKeyType="go"
          onSubmitEditing={handleResetPassword}
          editable={!isLoading}
          error={errors.confirmPassword || undefined}
          rightIcon={
            <AuthLink
              tone="muted"
              onPress={() => setShowConfirmPassword(!showConfirmPassword)}
              testID="reset-toggle-confirm"
              accessibilityLabel={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? 'Hide' : 'Show'}
            </AuthLink>
          }
        />

        <Button
          size="lg"
          fullWidth
          className="mt-2"
          onPress={handleResetPassword}
          loading={isLoading}
          testID="reset-submit"
        >
          Reset password
        </Button>
      </View>
    </AuthScreenLayout>
  );
}
